import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, FileText, Trophy, LogOut, Eye, Loader2, ExternalLink, Trash2, Star, Upload, Mail, Inbox, Search, X } from "lucide-react";
import api, { API, formatApiError } from "../lib/api";
import { uploadVideoChunked } from "../lib/videoUpload";
import PhotoCropper from "../components/PhotoCropper";
import GalleryPanel from "../components/GalleryPanel";
import AvpPanel from "../components/AvpPanel";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import ScreeningPanel from "../components/ScreeningPanel";
import ApplicantWorkflow from "../components/ApplicantWorkflow";
import FollowUpView from "../components/FollowUpView";

const STATUS_OPTS = ["new", "reviewing", "preview_sent", "preview_interested", "withdrawn", "interview", "qualification", "hired", "rejected"];
const STATUS_LABELS = {
  new: "New Applicant",
  reviewing: "Application Under Review",
  preview_sent: "Job Preview Sent – Awaiting Response",
  preview_interested: "Job Preview Completed – Interested",
  withdrawn: "Candidate Withdrew After Job Preview",
  interview: "Interview",
  qualification: "Qualification/Background Process",
  hired: "Hired",
  rejected: "Not Selected",
};
const STATUS_COLORS = {
  new: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  reviewing: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  preview_sent: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  preview_interested: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  withdrawn: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  interview: "bg-purple-500/15 text-purple-300 border-purple-500/30",
  qualification: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  hired: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const INACTIVE = ["withdrawn", "rejected"];
const POSITION_LABELS = {
  full_time: "Full-Time",
  experienced_fedex: "Experienced FedEx",
  part_time_priority: "PT Priority (legacy → AVP)",
};
const fmt = (d) => (d ? new Date(d).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—");

function ApplicationsPanel({ openReq, onOpenAvp }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [posFilter, setPosFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applications");
      setApps(data);
      setSelected((s) => (s ? data.find((x) => x.id === s.id) || s : s));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (openReq?.id) api.get("/admin/applications").then(({ data }) => { setApps(data); const a = data.find((x) => x.id === openReq.id); if (a) { setSelected(a); setNotes(a.admin_notes || ""); setFilter("all"); } }).catch(() => {}); }, [openReq]); // eslint-disable-line

  const filtered = apps.filter((a) => {
    const g = a.stage_summary?.group;
    if (filter === "active" && (INACTIVE.includes(a.status) || g === "closed" || g === "hired")) return false;
    if (filter === "hired" && g !== "hired") return false;
    if (filter === "followup" && !(g === "active" && !INACTIVE.includes(a.status) && ["unconfirmed", "interview", "first_advantage", "screening_tests"].includes(a.stage_summary?.key))) return false;
    if (!["all", "active", "hired", "followup"].includes(filter) && a.status !== filter) return false;
    if (posFilter !== "all" && a.position !== posFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${a.full_name} ${a.email} ${a.phone}`.toLowerCase().includes(s)) return false;
    }
    const day = (a.created_at || "").slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });
  const hasFilters = filter !== "active" || posFilter !== "all" || search || dateFrom || dateTo;
  const clearFilters = () => { setFilter("active"); setPosFilter("all"); setSearch(""); setDateFrom(""); setDateTo(""); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status, admin_notes: selected?.id === id ? notes : undefined });
      toast.success("Saved");
      await load();
      if (selected?.id === id) {
        const { data: fresh } = await api.get("/admin/applications");
        setSelected(fresh.find((a) => a.id === id) || null);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendReminder = async (id) => {
    try {
      await api.post(`/admin/applications/${id}/send-preview-reminder`);
      toast.success("Reminder email sent");
      const { data: fresh } = await api.get("/admin/applications");
      setApps(fresh);
      setSelected(fresh.find((a) => a.id === id) || null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convertToAvp = async (id) => {
    const shift = window.prompt("Convert this traditional applicant to the AVP workflow.\nType 'am' for 9 AM–1 PM, 'pm' for 4 PM–8 PM, or leave blank if unknown.\nNo e-mail is sent — the record moves to the AVP management-review queue and this traditional application is marked Withdrawn.");
    if (shift === null) return;
    const map = { am: "am_9_1", pm: "pm_4_8", "": "" };
    if (!(shift.trim().toLowerCase() in map)) return toast.error("Enter am, pm, or leave blank");
    try {
      await api.post(`/admin/applications/${id}/convert-to-avp`, { shift: map[shift.trim().toLowerCase()], send_info: false });
      toast.success("Converted to AVP — see the AVP Applicants tab (no e-mail sent)");
      const { data: fresh } = await api.get("/admin/applications");
      setApps(fresh); setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = (a) => { setSelected(a); setNotes(a.admin_notes || ""); };

  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="admin-apps-search" placeholder="Search name, email, phone" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64 bg-slate-950 border-slate-700 text-white" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="admin-apps-filter" className="w-64 bg-slate-950 border-slate-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            <SelectItem value="active">Active pipeline</SelectItem>
            <SelectItem value="followup">Needs follow-up (interview → screening)</SelectItem>
            <SelectItem value="hired">Completed hires</SelectItem>
            <SelectItem value="all">All (incl. withdrawn / not selected)</SelectItem>
            {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={posFilter} onValueChange={setPosFilter}>
          <SelectTrigger data-testid="admin-apps-position-filter" className="w-48 bg-slate-950 border-slate-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            <SelectItem value="all">All positions</SelectItem>
            {Object.entries(POSITION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Input data-testid="admin-apps-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 bg-slate-950 border-slate-700 text-white" />
          <span>to</span>
          <Input data-testid="admin-apps-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40 bg-slate-950 border-slate-700 text-white" />
        </div>
        {hasFilters && <Button data-testid="admin-apps-clear-filters" variant="ghost" className="text-slate-400 hover:text-white" onClick={clearFilters}><X className="w-4 h-4 mr-1" /> Clear</Button>}
        <Button data-testid="admin-apps-refresh" variant="outline" className="border-slate-700 text-slate-200" onClick={load}>Refresh</Button>
        {filter === "hired" && <a data-testid="admin-apps-export" href={`${API}/admin/hiring/export/completed?fmt=xlsx${filtered.length ? `&ids=${filtered.map((a) => a.id).join(",")}` : ""}`} className="inline-flex items-center h-9 px-3 rounded-md border border-emerald-500/40 text-emerald-200 text-sm">Export Completed Employee Information (.xlsx)</a>}
        <div data-testid="admin-apps-count" className="text-sm text-slate-400 ml-auto">Showing {filtered.length} of {apps.length}</div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-slate-500 text-sm">{apps.length === 0 ? "No applications yet." : "No applications match these filters."}</div>
      ) : (
        <div className="rounded-xl border border-slate-800 overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead className="bg-slate-950 text-slate-400 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">Applicant</th>
                <th className="text-left p-3">Position</th>
                <th className="text-left p-3">Application Date</th>
                <th className="text-left p-3">Current Stage</th>
                <th className="text-left p-3">Interest (history)</th>
                <th className="text-right p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`admin-app-row-${a.id}`} className={`border-t border-slate-800 hover:bg-slate-900/60 ${INACTIVE.includes(a.status) || a.stage_summary?.group === "closed" ? "opacity-60" : ""}`}>
                  <td className="p-3"><div className="font-semibold text-white">{a.full_name}</div><div className="text-xs text-slate-500">{a.email} · {a.phone}</div></td>
                  <td className="p-3 text-slate-300">{POSITION_LABELS[a.position] || a.position}</td>
                  <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{fmt(a.created_at)}</td>
                  <td className="p-3">
                    <span data-testid={`admin-app-status-badge-${a.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border whitespace-nowrap ${a.stage_summary?.group === "hired" ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" : a.stage_summary?.key === "unconfirmed" ? "bg-rose-500/10 text-rose-200 border-rose-500/40" : "bg-amber-500/10 text-amber-200 border-amber-500/30"}`}>{a.stage_summary?.label || STATUS_LABELS[a.status] || a.status}</span>
                  </td>
                  <td className="p-3 text-xs text-slate-500 whitespace-nowrap" data-testid={`admin-app-preview-response-${a.id}`}>{a.job_preview?.response === "interested" ? `Interested ${fmt(a.job_preview.response_at)}` : a.job_preview?.response === "withdraw" ? `Withdrew ${fmt(a.job_preview.response_at)}` : a.job_preview?.sent_at ? "Preview sent — awaiting" : "—"}</td>
                  <td className="p-3 text-right">
                    <Button data-testid={`admin-app-view-${a.id}`} size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => openDetail(a)}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="display uppercase text-2xl">{selected.full_name}</DialogTitle>
              </DialogHeader>
              <ApplicantWorkflow kind="applications" recordId={selected.id} onChanged={load} onOpenAvp={onOpenAvp}
                legacyPanel={<ScreeningPanel kind="applications" recordId={selected.id} />}
                steps={{
                  application: (
                    <div className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Position</span><div>{POSITION_LABELS[selected.position]}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Submitted</span><div>{new Date(selected.created_at).toLocaleString()}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Email</span><div>{selected.email}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Phone</span><div>{selected.phone}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Location</span><div>{[selected.city, selected.state, selected.zip_code].filter(Boolean).join(", ") || "—"}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Years driving</span><div>{selected.years_driving}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Delivery exp</span><div>{selected.has_delivery_experience ? "Yes" : "No"}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">CDL</span><div>{selected.has_cdl ? "Yes" : "No"}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">DOT medical certificate (applicant answer)</span><div>{selected.dot_medical_certificate || "—"}{selected.dot_medical_expiration ? ` · exp ${selected.dot_medical_expiration}` : ""}</div></div>
                        <div><span className="text-slate-500 uppercase text-xs tracking-widest">Availability</span><div>{selected.availability?.join(", ") || "—"}</div></div>
                        {selected.delivery_experience_detail && <div className="md:col-span-2"><span className="text-slate-500 uppercase text-xs tracking-widest">Experience detail</span><div className="whitespace-pre-line">{selected.delivery_experience_detail}</div></div>}
                        {selected.notes && <div className="md:col-span-2"><span className="text-slate-500 uppercase text-xs tracking-widest">Notes</span><div className="whitespace-pre-line">{selected.notes}</div></div>}
                        <div className="md:col-span-2"><span className="text-slate-500 uppercase text-xs tracking-widest">Referral</span><div>{selected.referral_source || "—"}</div></div>
                      </div>
                      {selected.resume_file_id && <a data-testid="admin-app-resume-link" href={`${API}/files/${selected.resume_file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-semibold"><ExternalLink className="w-4 h-4" /> Open resume</a>}
                      <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2">
                        <span className="text-xs text-slate-400">Traditional driver workflow (Day-in-the-Life videos). {selected.converted_avp_id ? <span className="text-amber-300">Converted to AVP.</span> : "Applicant wants the own-vehicle AVP program instead?"}</span>
                        {!selected.converted_avp_id && <Button data-testid="admin-app-convert-avp" size="sm" variant="outline" className="border-amber-500/40 text-amber-300" onClick={() => convertToAvp(selected.id)}>Convert to AVP</Button>}
                      </div>
                      <div><Label>Admin notes</Label><Textarea data-testid="admin-app-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" rows={2} />
                        <Button data-testid="admin-app-notes-save" size="sm" variant="outline" className="mt-2 border-slate-700 text-slate-200" onClick={() => updateStatus(selected.id, selected.status)}>Save notes</Button></div>
                      <div className="text-[11px] text-slate-500">Decision to proceed: use <strong>Preview & Send Next Update → Position Information</strong>. Not proceeding: use <strong>Update Current Stage → Closed</strong> with a reason.</div>
                    </div>),
                  preview: (
                    <div data-testid="admin-app-preview-panel" className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 grid sm:grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500 uppercase text-xs tracking-widest">Job Preview (Day-in-the-Life videos) sent</span><div>{selected.job_preview?.sent_at ? `${fmt(selected.job_preview.sent_at)} to ${selected.job_preview.sent_to}${selected.job_preview.send_count > 1 ? ` (sent ${selected.job_preview.send_count}×)` : ""}` : "Not sent — use Preview & Send Next Update → Position Information."}</div></div>
                      <div><span className="text-slate-500 uppercase text-xs tracking-widest">Candidate response</span><div data-testid="admin-app-preview-response-detail">{selected.job_preview?.response === "interested" ? <span className="text-emerald-300">Still interested · {fmt(selected.job_preview.response_at)}</span> : selected.job_preview?.response === "withdraw" ? <span className="text-rose-300">Withdrew · {fmt(selected.job_preview.response_at)}</span> : selected.job_preview?.sent_at ? <span className="text-amber-300">Awaiting response</span> : "—"}</div></div>
                      {selected.job_preview?.sent_at && !selected.job_preview?.response && <div className="sm:col-span-2 flex items-center justify-between gap-3 flex-wrap">
                        <div data-testid="admin-app-reminder-status" className="text-xs text-slate-400">{selected.job_preview?.reminder_sent_at ? `Automatic reminder sent ${fmt(selected.job_preview.reminder_sent_at)} (sent once)` : "Automatic reminder goes out 3 days after the preview if no response (sent once)."}</div>
                        {!selected.job_preview?.reminder_sent_at && <Button data-testid="admin-app-send-reminder" size="sm" variant="ghost" className="text-amber-300 hover:text-amber-200" onClick={() => window.confirm("Send the job-preview reminder now?") && sendReminder(selected.id)}>Send reminder now</Button>}
                      </div>}
                    </div>),
                }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EMPTY_DRIVER = { name: "", week_of: "", route_zone: "", achievements: "", safety_record: "", manager_quote: "", photo_file_id: null, video_url: "", video_file_id: null, is_current: true };

function DriversPanel() {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState(EMPTY_DRIVER);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const { data } = await api.get("/drivers"); setDrivers(data); } catch (e) { toast.error(formatApiError(e)); } };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCropFile(file);
  };

  const uploadPhoto = async (file) => {
    setCropFile(null);
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/driver-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_file_id", data.file_id);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const handleVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) return toast.error("Video must be 200MB or smaller");
    setVideoProgress(0);
    try {
      const data = await uploadVideoChunked(file, setVideoProgress);
      setForm((f) => ({ ...f, video_file_id: data.file_id, video_url: "" }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVideoProgress(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.week_of || !form.achievements) return toast.error("Name, week, and achievements are required");
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/drivers/${editingId}`, form);
      else await api.post("/admin/drivers", form);
      toast.success("Saved");
      setForm(EMPTY_DRIVER); setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const edit = (d) => { setEditingId(d.id); setForm({ ...EMPTY_DRIVER, ...d }); window.scrollTo({ top: 200, behavior: "smooth" }); };

  const remove = async (id) => {
    if (!window.confirm("Delete this driver record?")) return;
    try { await api.delete(`/admin/drivers/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid md:grid-cols-5 gap-6">
      {cropFile && <PhotoCropper file={cropFile} onDone={uploadPhoto} onCancel={() => setCropFile(null)} />}
      <form onSubmit={save} className="md:col-span-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 space-y-4">
        <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">{editingId ? "Edit Recognition" : "New Recognition"}</div>
        <div>
          <Label>Driver name *</Label>
          <Input data-testid="driver-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
        </div>
        <div>
          <Label>Week of *</Label>
          <Input data-testid="driver-form-week" type="date" value={form.week_of} onChange={(e) => set("week_of", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
        </div>
        <div>
          <Label>Route / zone</Label>
          <Input data-testid="driver-form-zone" value={form.route_zone} onChange={(e) => set("route_zone", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" placeholder="Baltimore County · Route 22" />
        </div>
        <div>
          <Label>Achievements *</Label>
          <Textarea data-testid="driver-form-achievements" rows={4} value={form.achievements} onChange={(e) => set("achievements", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
        </div>
        <div>
          <Label>Safety record</Label>
          <Textarea data-testid="driver-form-safety" rows={2} value={form.safety_record} onChange={(e) => set("safety_record", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
        </div>
        <div>
          <Label>Manager quote</Label>
          <Textarea data-testid="driver-form-quote" rows={2} value={form.manager_quote} onChange={(e) => set("manager_quote", e.target.value)} className="mt-2 bg-slate-950 border-slate-700 text-white" />
        </div>
        <div>
          <Label>Photo</Label>
          <label className="mt-2 flex items-center gap-2 cursor-pointer rounded-md border border-dashed border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {form.photo_file_id ? "Replace photo" : "Upload photo"}
            <input data-testid="driver-form-photo-input" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
          </label>
          {form.photo_file_id && (
            <div className="mt-2 flex items-center gap-3">
              <img src={`${API}/files/${form.photo_file_id}/download`} alt="Driver" data-testid="driver-form-photo-preview" className="w-16 h-16 rounded-lg object-cover border border-slate-700" />
              <div className="text-xs text-emerald-400">✓ Photo attached</div>
            </div>
          )}
        </div>
        <div>
          <Label>Recognition video</Label>
          <label className="mt-2 flex items-center gap-2 cursor-pointer rounded-md border border-dashed border-slate-600 bg-slate-950 px-3 py-2 text-sm text-slate-200 hover:border-amber-500/60">
            {videoProgress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {videoProgress !== null ? `Uploading… ${videoProgress}%` : form.video_file_id ? "Replace video" : "Upload video (MP4/MOV, up to 200MB)"}
            <input data-testid="driver-form-video-input" type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleVideo} disabled={videoProgress !== null} />
          </label>
          {videoProgress !== null && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden"><div data-testid="driver-form-video-progress" className="h-full bg-amber-500 transition-all" style={{ width: `${videoProgress}%` }} /></div>
          )}
          {form.video_file_id && (
            <div className="text-xs text-emerald-400 mt-1 flex items-center gap-3">✓ Video attached
              <button type="button" data-testid="driver-form-video-remove" className="text-slate-400 hover:text-red-400 underline" onClick={() => set("video_file_id", null)}>remove</button>
            </div>
          )}
          <div className="mt-3 text-[11px] uppercase tracking-widest text-slate-500">or paste a link</div>
          <Input data-testid="driver-form-video-url" value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} className="mt-1 bg-slate-950 border-slate-700 text-white" placeholder="https://youtube.com/... or .mp4 link" />
        </div>
        <label className="flex items-center gap-2">
          <Checkbox data-testid="driver-form-is-current" checked={form.is_current} onCheckedChange={(v) => set("is_current", !!v)} />
          <span className="text-sm text-slate-200">Feature as current Driver of the Week</span>
        </label>
        <div className="flex gap-2">
          <Button data-testid="driver-form-submit" type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingId ? "Update" : "Publish"}</Button>
          {editingId && <Button data-testid="driver-form-cancel" type="button" variant="outline" className="border-slate-700 text-slate-200" onClick={() => { setEditingId(null); setForm(EMPTY_DRIVER); }}>Cancel</Button>}
        </div>
      </form>

      <div className="md:col-span-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">All Recognitions ({drivers.length})</div>
        {drivers.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center text-slate-500 text-sm">No drivers published yet.</div>
        ) : (
          <div className="space-y-3">
            {drivers.map((d) => (
              <div key={d.id} data-testid={`admin-driver-row-${d.id}`} className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-950 flex-shrink-0">
                  {d.photo_file_id ? <img src={`${API}/files/${d.photo_file_id}/download`} className="w-full h-full object-cover" alt={d.name} /> : <div className="w-full h-full flex items-center justify-center text-slate-700"><Trophy /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold text-white truncate">{d.name}</div>
                    {d.is_current && <Badge className="bg-amber-500 text-slate-950 uppercase text-[10px]"><Star className="w-3 h-3 mr-0.5" /> Current</Badge>}
                  </div>
                  <div className="text-xs text-slate-400">Week of {d.week_of} {d.route_zone ? `· ${d.route_zone}` : ""}</div>
                </div>
                <Button data-testid={`admin-driver-edit-${d.id}`} size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => edit(d)}>Edit</Button>
                <Button data-testid={`admin-driver-delete-${d.id}`} size="sm" variant="outline" className="border-rose-700 text-rose-300 hover:bg-rose-950" onClick={() => remove(d.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/contacts"); setItems(data); } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { try { await api.patch(`/admin/contacts/${id}/read`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  if (items.length === 0) return <div className="py-16 text-center text-slate-500 text-sm">No contact messages yet.</div>;

  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} data-testid={`admin-contact-${c.id}`} className={`rounded-xl border p-5 ${c.is_read ? "border-slate-800 bg-slate-900/40" : "border-amber-500/40 bg-amber-500/5"}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-white font-bold">{c.name} <span className="text-xs text-slate-500 font-normal">· {c.email}{c.phone ? ` · ${c.phone}` : ""}</span></div>
              <div className="text-sm text-amber-400 mt-0.5">{c.subject}</div>
            </div>
            <div className="text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</div>
          </div>
          <p className="mt-3 text-slate-300 text-sm whitespace-pre-line">{c.message}</p>
          {!c.is_read && (
            <div className="mt-3"><Button data-testid={`admin-contact-mark-read-${c.id}`} size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => markRead(c.id)}>Mark as read</Button></div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [optionA, setOptionA] = useState(false);
  useEffect(() => { api.get("/admin/hiring/features").then(({ data }) => setOptionA(!!data.option_a)).catch(() => {}); }, []);
  const [stats, setStats] = useState({ total: 0, new: 0, interview: 0, hired: 0 });

  const hrOnly = user?.role === "recruiting_hr";
  const [tab, setTab] = useState("applications");
  const [openReq, setOpenReq] = useState(null);
  const openFromQueue = (r) => { setOpenReq({ kind: r.kind, id: r.id, n: Date.now() }); setTab(r.kind === "avp" ? "avp" : "applications"); };
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400 font-semibold">{hrOnly ? "HR / Recruiting Compliance" : "Admin Dashboard"}</div>
          <h1 className="mt-2 text-3xl md:text-4xl font-extrabold uppercase display text-white">{hrOnly ? "Recruiting Dashboard" : "Operations Portal"}</h1>
          <div className="text-sm text-slate-400 mt-1">Signed in as {user?.email}</div>
          {user?.must_change_password && <div data-testid="admin-must-change-pw" className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-100">Please set a personal password now using “Change password”.</div>}
        </div>
        <div className="flex items-center gap-2">
          {optionA && <a data-testid="open-pipeline-link" href="/admin/pipeline" className="rounded-full bg-amber-500 text-slate-950 text-xs font-bold px-3 py-1.5 hover:bg-amber-400">Recruiting Pipeline (new)</a>}
          <ChangePasswordDialog />
          <Button data-testid="admin-dashboard-logout" variant="outline" className="border-slate-700 text-slate-200" onClick={logout}><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "Total Applications", v: stats.total, Icon: Users },
          { l: "New", v: stats.new, Icon: Inbox },
          { l: "In Interview", v: stats.interview, Icon: FileText },
          { l: "Hired", v: stats.hired, Icon: Trophy },
        ].map((s) => (
          <div key={s.l} data-testid={`admin-stat-${s.l.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400"><s.Icon className="w-3.5 h-3.5" />{s.l}</div>
            <div className="mt-2 text-3xl font-extrabold text-white display">{s.v}</div>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-slate-900 border border-slate-800 flex flex-wrap h-auto max-w-full">
          <TabsTrigger data-testid="admin-tab-applications" value="applications">Applications</TabsTrigger>
          {!hrOnly && <TabsTrigger data-testid="admin-tab-drivers" value="drivers">Driver of the Week</TabsTrigger>}
          {!hrOnly && <TabsTrigger data-testid="admin-tab-contacts" value="contacts">Messages</TabsTrigger>}
          <TabsTrigger data-testid="admin-tab-avp" value="avp">AVP Applicants</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-followup" value="followup">Needs Follow-Up</TabsTrigger>
          {!hrOnly && <TabsTrigger data-testid="admin-tab-gallery" value="gallery">Team Gallery</TabsTrigger>}
        </TabsList>
        <TabsContent value="applications" className="mt-6"><ApplicationsPanel openReq={openReq?.kind === "applications" ? openReq : null} onOpenAvp={(id) => openFromQueue({ kind: "avp", id })} /></TabsContent>
        <TabsContent value="drivers" className="mt-6"><DriversPanel /></TabsContent>
        <TabsContent value="contacts" className="mt-6"><ContactsPanel /></TabsContent>
        <TabsContent value="avp" className="mt-6"><AvpPanel openReq={openReq?.kind === "avp" ? openReq : null} /></TabsContent>
        <TabsContent value="followup" className="mt-6"><FollowUpView onOpen={openFromQueue} /></TabsContent>
        <TabsContent value="gallery" className="mt-6"><GalleryPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
